export interface ResolvedLeaderboardColumn {
  field: string;
  display: DriverLeaderboardFieldDisplay;
}

// The field picker reorders `fieldAliases` on drag (not `fields`), so the alias
// order is the configured column order. The picker aliases every field it
// adds; if any field lacks an alias the list isn't authoritative, so fall back
// to `fields` order.
export const getOrderedFields = ({
  fields,
  fieldAliases,
}: {
  fields: string[];
  fieldAliases?: { field: string; alias: string }[];
}): string[] => {
  const aliased = (fieldAliases ?? [])
    .map((a) => a.field)
    .filter((f, i, all) => fields.includes(f) && all.indexOf(f) === i);
  return aliased.length === fields.length ? aliased : fields;
};

// A field's display: its explicit `fieldDisplays` entry; else, for slides saved
// before per-field displays, the legacy name/value roles; else `standard`.
export const getFieldDisplay = ({
  slide,
  field,
}: {
  slide: Pick<
    DriverLeaderboardSlide,
    'fieldDisplays' | 'nameField' | 'valueField'
  >;
  field: string;
}): DriverLeaderboardFieldDisplay => {
  if (slide.fieldDisplays) {
    return (
      slide.fieldDisplays.find((d) => d.field === field)?.display ?? 'standard'
    );
  }
  if (field === slide.nameField) {
    return 'name';
  }
  if (field === slide.valueField) {
    return 'headline';
  }
  return 'standard';
};

// The synthetic column a dynamic rank (`slide.showRank`, ranked by the sort)
// renders in. Not a dataset field: its values come from computeDynamicRanks.
export const DYNAMIC_RANK_FIELD = '__dynamicRank';

type DynamicRankConfig = Pick<DriverLeaderboardSlide, 'showRank'> & {
  sort?: SortField[];
};

// A rank needs something to rank by, so it's off without a sort.
export const hasDynamicRank = (slide: DynamicRankConfig): boolean =>
  !!slide.showRank && !!slide.sort && slide.sort.length > 0;

// Ordered columns with their display. Only one column may render as the rank
// (it drives the podium tints and Rank Change). A dynamic rank takes it, as a
// leading synthetic column; otherwise the first rank badge field wins.
const resolveColumns = (
  slide: Pick<
    DriverLeaderboardSlide,
    'fields' | 'fieldAliases' | 'fieldDisplays' | 'nameField' | 'valueField'
  > &
    DynamicRankConfig,
): {
  columns: ResolvedLeaderboardColumn[];
  rankField?: string;
  isDynamicRank: boolean;
} => {
  const isDynamicRank = hasDynamicRank(slide);
  let rankField: string | undefined;
  const fieldColumns = getOrderedFields(slide).map((field) => {
    const display = getFieldDisplay({ slide, field });
    if (display === 'rank') {
      if (isDynamicRank || rankField !== undefined) {
        return { field, display: 'standard' as const };
      }
      rankField = field;
    }
    return { field, display };
  });
  if (isDynamicRank) {
    return {
      columns: [
        { field: DYNAMIC_RANK_FIELD, display: 'rank' },
        ...fieldColumns,
      ],
      isDynamicRank,
    };
  }
  return { columns: fieldColumns, rankField, isDynamicRank };
};

export default resolveColumns;
